import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Badge,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  AttachMoney as AttachMoneyIcon,
  VideoCall as VideoCallIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useAuthToken } from "../TokenContext";

const ManagerInterviewDashboard = ({collapsed}) => {
  // State management
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRequests, setTotalRequests] = useState(0);
  
  // NEW: Loading states for specific actions
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [loadingRequestId, setLoadingRequestId] = useState(null);
  
  const { user } = useSelector(state => state.auth);
  const token = useAuthToken();
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Safe access to user data with null checks
  const managerId = user?.data?.[0]?.emp_id;
  const clientCode = user?.data?.[0]?.client_code;

  // Debug logging
  useEffect(() => {
    console.log('User data:', user);
    console.log('Manager ID:', managerId);
    console.log('Client Code:', clientCode);
    console.log('Token:', token ? 'Token exists' : 'No token');
  }, [user, managerId, clientCode, token]);

  // Load interview requests
  const loadInterviewRequests = async (status = 'all', pageNum = 0) => {
    // Enhanced validation
    if (!managerId || !clientCode) {
      console.error('Missing required data:', { managerId, clientCode, user });
      setSnackbar({
        open: true,
        message: 'Manager ID or Client Code not found. Please login again.',
        severity: 'error'
      });
      setLoading(false);
      return;
    }

    if (!token) {
      console.error('No authentication token found');
      setSnackbar({
        open: true,
        message: 'Authentication token not found. Please login again.',
        severity: 'error'
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        manager_id: managerId,
        client_code: clientCode,
        page: pageNum + 1,
        limit: rowsPerPage
      };

      if (status !== 'all') {
        requestData.status = status;
      }

      console.log('API Request Data:', requestData);
      console.log('API Headers:', { Authorization: token });

      const response = await axios.post(
        process.env.REACT_APP_CLIENT_MANAGERETINTERVIEWREQUESTS,
        requestData,
        { 
          headers: { 
            'Authorization': token, 
            'Content-Type': 'application/json' 
          } 
        }
      );

      console.log('API Response:', response.data);

      if (response.data.status === 200) {
       setRequests(response.data.data.requests || []);
        setTotalRequests(response.data.total || response.data.data?.length || 0);
        setSnackbar({
          open: true,
          message: `Loaded ${response.data.data?.length || 0} interview requests`,
          severity: 'success'
        });
      } else {
        console.error('API Error Response:', response.data);
        setSnackbar({
          open: true,
          message: response.data.message || response.data.data || 'Failed to load interview requests',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error loading requests:', error);
      
      // More detailed error handling
      let errorMessage = 'Failed to load interview requests. ';
      if (error.response) {
        console.error('Error Response:', error.response.data);
        errorMessage += `Server error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        console.error('No response received:', error.request);
        errorMessage += 'No response from server. Please check your connection.';
      } else {
        console.error('Request setup error:', error.message);
        errorMessage += error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Get request details - UPDATED with loader
  const getRequestDetails = async (requestId) => {
    if (!managerId || !clientCode || !token) {
      setSnackbar({
        open: true,
        message: 'Missing authentication data. Please login again.',
        severity: 'error'
      });
      return;
    }

    // Set loading states
    setLoadingDetails(true);
    setLoadingRequestId(requestId);

    try {
      const requestData = {
        request_id: requestId,
        manager_id: managerId,
        client_code: clientCode,
      };

      console.log('Getting request details:', requestData);

      const response = await axios.post(
        process.env.REACT_APP_CLIENT_MANAGERETINTERVIEWREQUESTDETAILS,
        requestData,
        { 
          headers: { 
            'Authorization': token, 
            'Content-Type': 'application/json' 
          } 
        }
      );

      console.log('Details API Response:', response.data);

      if (response.data.status === 200) {
        setSelectedRequest(response.data.data);
        setDetailsOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || response.data.data || 'Failed to load request details',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error loading request details:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load request details. Please try again.',
        severity: 'error'
      });
    } finally {
      // Clear loading states
      setLoadingDetails(false);
      setLoadingRequestId(null);
    }
  };

  // Cancel interview request - UPDATED with loader
  const cancelInterviewRequest = async (requestId) => {
    if (!managerId || !clientCode || !token) {
      setSnackbar({
        open: true,
        message: 'Missing authentication data. Please login again.',
        severity: 'error'
      });
      return;
    }

    // Set loading state for cancelling
    setCancelling(true);

    try {
      const requestData = {
        request_id: requestId,
        manager_id: managerId,
        client_code: clientCode,
      };

      console.log('Cancelling request:', requestData);

      const response = await axios.post(
        process.env.REACT_APP_CLIENT_MANAGERCANCELINTERVIEWREQUESTDETAILS,
        requestData,
        { 
          headers: { 
            'Authorization': token, 
            'Content-Type': 'application/json' 
          } 
        }
      );

      console.log('Cancel API Response:', response.data);

      if (response.data.status === 200) {
        setSnackbar({
          open: true,
          message: 'Interview request cancelled successfully!',
          severity: 'success'
        });
        // Refresh the list
        loadInterviewRequests(statusFilter, page);
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || response.data.data || 'Failed to cancel request',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error cancelling request:', error);
      setSnackbar({
        open: true,
        message: 'Failed to cancel request. Please try again.',
        severity: 'error'
      });
    } finally {
      // Clear loading state
      setCancelling(false);
    }
    setCancelConfirmOpen(false);
    setRequestToCancel(null);
  };

  // Load requests on component mount and when filters change
  useEffect(() => {
    // Only load if we have the required data
    if (managerId && clientCode && token) {
      loadInterviewRequests(statusFilter, page);
    } else {
      console.log('Waiting for authentication data...', { managerId, clientCode, token: !!token });
      setLoading(false);
    }
  }, [statusFilter, page, rowsPerPage, managerId, clientCode, token]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    const statusMap = ['all', 'pending', 'accepted', 'declined', 'cancelled'];
    setStatusFilter(statusMap[newValue]);
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'success';
      case 'declined': return 'error';
      case 'cancelled': return 'default';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  // Get urgency chip color
  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  // Get interview type icon
  const getInterviewTypeIcon = (type) => {
    switch (type) {
      case 'Video Call': return <VideoCallIcon />;
      case 'Phone Call': return <PhoneIcon />;
      case 'In-Person': return <LocationOnIcon />;
      case 'Online Assessment': return <ComputerIcon />;
      default: return <VideoCallIcon />;
    }
  };

  // Stats calculation
  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    accepted: requests.filter(r => r.status === 'accepted').length,
    declined: requests.filter(r => r.status === 'declined').length
  };

  //Show loading or error state if missing required data
  if (!managerId || !clientCode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body2">
            Manager ID or Client Code not found. Please login again.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Debug info: Manager ID: {managerId || 'missing'}, Client Code: {clientCode || 'missing'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!token) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Authentication Token Missing
          </Typography>
          <Typography variant="body2">
            Please login again to access the dashboard.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 , maxWidth: '100%',
  marginLeft: collapsed ? "60px" : "240px",}}  >
      {/* Debug Info (remove in production) */}
      {/* <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Debug: Manager ID: {managerId}, Client Code: {clientCode}, Token: {token ? 'Present' : 'Missing'}
        </Typography>
      </Box> */}

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Interview Requests Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => loadInterviewRequests(statusFilter, page)}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Requests
              </Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">{stats.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Accepted
              </Typography>
              <Typography variant="h4" color="success.main">{stats.accepted}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Declined
              </Typography>
              <Typography variant="h4" color="error.main">{stats.declined}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
          <Tab label={`All (${stats.total})`} />
          <Tab label={
            <Badge badgeContent={stats.pending} color="warning">
              Pending
            </Badge>
          } />
          <Tab label={`Accepted (${stats.accepted})`} />
          <Tab label={`Declined (${stats.declined})`} />
          <Tab label="Cancelled" />
        </Tabs>
      </Paper>

      {/* Requests Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading interview requests...</Typography>
            </Box>
          ) : requests.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h6" color="textSecondary">
                No interview requests found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {statusFilter !== 'all' ? `No ${statusFilter} requests at the moment.` : 'You haven\'t sent any interview requests yet.'}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Candidate</TableCell>
                      <TableCell>Project</TableCell>
                      <TableCell>Interview Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Urgency</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="subtitle2">
                                {request.candidate_name || 'Unknown Candidate'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {request.candidate_email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {request.project_title}
                          </Typography>
                          {request.compensation && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <AttachMoneyIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {request.compensation}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getInterviewTypeIcon(request.interview_type)}
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {request.interview_type}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {request.estimated_duration}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={request.status.toUpperCase()}
                            color={getStatusColor(request.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={request.urgency.toUpperCase()}
                            color={getUrgencyColor(request.urgency)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(request.created_at).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(request.created_at).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {/* UPDATED: View Details Button with Loader */}
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => getRequestDetails(request.id)}
                              color="primary"
                              disabled={loadingRequestId === request.id}
                            >
                              {loadingRequestId === request.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                          {request.status === 'pending' && (
                            <Tooltip title="Cancel Request">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setRequestToCancel(request);
                                  setCancelConfirmOpen(true);
                                }}
                                color="error"
                                disabled={loadingRequestId === request.id}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalRequests}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Request Details Dialog - UPDATED with loader */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Interview Request Details
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading request details...</Typography>
            </Box>
          ) : selectedRequest && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Candidate Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Name"
                        secondary={selectedRequest.candidate_name || 'Not provided'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Email"
                        secondary={selectedRequest.candidate_email || 'Not provided'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Hourly Rate"
                        secondary={selectedRequest.hourly_rate ? `${selectedRequest.currency || '$'} ${selectedRequest.hourly_rate}` : 'Not specified'}
                      />
                    </ListItem>
                  </List>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Interview Details</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Project Title"
                        secondary={selectedRequest.project_title}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Interview Type"
                        secondary={selectedRequest.interview_type}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Estimated Duration"
                        secondary={selectedRequest.estimated_duration}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Compensation"
                        secondary={selectedRequest.compensation || 'Not specified'}
                      />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Preferred Schedule</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Preferred Dates:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedRequest.preferred_dates?.map((date, index) => (
                        <Chip key={index} label={date} size="small" />
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Preferred Time Slots:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedRequest.preferred_time_slots?.map((slot, index) => (
                        <Chip key={index} label={slot} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                </Grid>

                {selectedRequest.message && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Message</Typography>
                    <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                      <Typography variant="body2">{selectedRequest.message}</Typography>
                    </Paper>
                  </Grid>
                )}

                {selectedRequest.response_message && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Candidate Response</Typography>
                    <Paper sx={{ p: 2, backgroundColor: selectedRequest.status === 'accepted' ? 'success.50' : 'error.50' }}>
                      <Typography variant="body2">{selectedRequest.response_message}</Typography>
                      {selectedRequest.responded_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Responded on: {new Date(selectedRequest.responded_at).toLocaleString()}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Chip
                        label={`Status: ${selectedRequest.status.toUpperCase()}`}
                        color={getStatusColor(selectedRequest.status)}
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={`Priority: ${selectedRequest.urgency.toUpperCase()}`}
                        color={getUrgencyColor(selectedRequest.urgency)}
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(selectedRequest.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDetailsOpen(false)}
            disabled={loadingDetails}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog - UPDATED with loader */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => !cancelling && setCancelConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Interview Request</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel the interview request for{' '}
            <strong>{requestToCancel?.candidate_name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
          {cancelling && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Cancelling request...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCancelConfirmOpen(false)}
            disabled={cancelling}
          >
            Keep Request
          </Button>
          <Button 
            onClick={() => cancelInterviewRequest(requestToCancel?.id)}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} /> : null}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManagerInterviewDashboard;